/**
 * Counts how many pages in the inclusive range [from, to] have NOT been printed yet.
 * @param {number} from - First page of the range
 * @param {number} to - Last page of the range
 * @param {number[]} alreadyPrinted - Unique page numbers already printed by the user
 * @returns {number} Count of pages in the range that are new
 */
function countNewPages(from, to, alreadyPrinted) {
    const printedSet = new Set(alreadyPrinted);
    let newPages = 0;
    for (let page = from; page <= to; page += 1) {
        if (!printedSet.has(page)) {
            newPages += 1;
        }
    }
    return newPages;
}

/**
 * Validates a print range against the per-job and lifetime quotas.
 *
 * The "new pages" count is a client-side estimate for UX (to enable/disable the
 * print button before sending). The authoritative unique-page count and discount
 * live in the backend.
 *
 * @param {{ pageFrom: number, pageTo: number }} range - Requested page range
 * @param {Object} quota - Quota context
 * @param {number} quota.totalBookPages - Total pages of the book (0/undefined if unknown)
 * @param {number} quota.printLimitPerJob - Max pages per print job (drm_print_limit)
 * @param {number} quota.remainingPages - Remaining lifetime pages for this user
 * @param {number[]} [quota.printedPages] - Unique pages already printed by the user
 * @returns {{ isValid: boolean, errorKey: ('invalidRange'|'jobLimit'|'lifetime'|null), newPagesCount: number, requestedCount: number }}
 */
export function validatePrintRange(range, quota) {
    const { pageFrom, pageTo } = range;
    const { totalBookPages, printLimitPerJob, remainingPages, printedPages = [] } = quota;

    const hasKnownTotal = Number.isInteger(totalBookPages) && totalBookPages > 0;
    const isWithinBook =
        Number.isInteger(pageFrom) &&
        Number.isInteger(pageTo) &&
        pageFrom >= 1 &&
        pageFrom <= pageTo &&
        (!hasKnownTotal || pageTo <= totalBookPages);

    if (!isWithinBook) {
        return { isValid: false, errorKey: 'invalidRange', newPagesCount: 0, requestedCount: 0 };
    }

    const requestedCount = pageTo - pageFrom + 1;
    if (requestedCount > printLimitPerJob) {
        return { isValid: false, errorKey: 'jobLimit', newPagesCount: 0, requestedCount };
    }

    const newPagesCount = countNewPages(pageFrom, pageTo, printedPages);
    if (newPagesCount > remainingPages) {
        return { isValid: false, errorKey: 'lifetime', newPagesCount, requestedCount };
    }

    return { isValid: true, errorKey: null, newPagesCount, requestedCount };
}
